import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Rating } from '@mui/material'
import InitialsAvatar from 'src/components/elements/initials-avatar/InitialsAvatar'
import { Button } from 'src/components/ui'
import { useGetUserReviewsQuery } from 'src/redux/api'
import LeaveReviewForm from './LeaveReviewForm'
import ReviewReportModal from './ReviewReportModal'
import { formatDate } from 'src/helpers/dateHelpers'

interface IReviewsBlock {
  propertyId: string
  revieweeId: string
  // Whether to offer the "leave a review" form — only after a real two-sided
  // interaction (server-gated, mirrored here so the form isn't shown in vain).
  canLeave: boolean
  // Logged-in non-owner who has NOT interacted yet — show why the form is absent.
  needsInteraction?: boolean
  // The viewer is the person these reviews are about — they may report each one.
  isReviewee?: boolean
}

// Ratings & reviews block for the agent card (roadmap 41 H1/B4 + rework): the
// score, an expandable list (anonymous authors masked), the leave-review form
// (only when eligible), and per-review reporting for the reviewee.
const ReviewsBlock: FC<IReviewsBlock> = ({
  propertyId,
  revieweeId,
  canLeave,
  needsInteraction,
  isReviewee,
}) => {
  const { t } = useTranslation()
  const { data } = useGetUserReviewsQuery(revieweeId)
  const [showList, setShowList] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [reportId, setReportId] = useState<string | null>(null)

  const average = data?.average ?? 0
  const count = data?.count ?? 0
  const items = data?.items ?? []

  return (
    <div className="flex flex-col gap-2 w-full pt-4 border-0 border-t border-solid border-ink/[0.08]">
      <div className="flex items-center gap-2 flex-wrap">
        <Rating value={average} precision={0.5} readOnly size="small" />
        <span className="text-sm font-black text-ink">{average.toFixed(1)}</span>
        <button
          type="button"
          className="min-h-8 text-sm font-bold text-brand-ink hover:underline disabled:font-normal disabled:text-ink-2 disabled:no-underline"
          disabled={!count}
          onClick={() => setShowList((v) => !v)}
        >
          {count ? t('review.count', { count }) : t('review.noReviews')}
        </button>
      </div>

      {showList && items.length > 0 && (
        <ul className="flex flex-col gap-2 m-0 p-0 list-none">
          {items.map((review) => {
            const displayName = review.anonymous ? t('review.anonymous') : review.name
            return (
              <li key={review.id} className="flex flex-col gap-1 bg-page rounded-xl px-3 py-2.5">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-6 h-6 rounded-full overflow-hidden flex-shrink-0">
                    <InitialsAvatar src={review.avatar} name={displayName} />
                  </div>
                  <span className="truncate text-sm font-bold text-ink">{displayName}</span>
                  <Rating value={review.rating} readOnly size="small" className="!ml-auto" />
                </div>
                {review.comment && (
                  <p className="m-0 text-sm leading-5 text-ink break-words">{review.comment}</p>
                )}
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-ink-2">{formatDate(review.createdAt)}</span>
                  {isReviewee && (
                    <button
                      type="button"
                      onClick={() => setReportId(review.id)}
                      className="min-h-8 text-xs text-ink-2 hover:text-danger transition-colors"
                    >
                      {t('review.report')}
                    </button>
                  )}
                </div>
              </li>
            )
          })}
        </ul>
      )}

      {canLeave && !showForm && (
        <Button type="button" size="sm" variant="outlined" onClick={() => setShowForm(true)}>
          {t('review.leave')}
        </Button>
      )}
      {canLeave && showForm && (
        <LeaveReviewForm
          propertyId={propertyId}
          revieweeId={revieweeId}
          onDone={() => setShowForm(false)}
        />
      )}
      {!canLeave && needsInteraction && (
        <p className="m-0 text-xs leading-4 text-ink-2">{t('review.needConversation')}</p>
      )}

      <ReviewReportModal
        reviewId={reportId || ''}
        open={!!reportId}
        onClose={() => setReportId(null)}
      />
    </div>
  )
}

export default ReviewsBlock
